import * as Sentry from '@sentry/react'
import {useEffect} from 'react'
import {createRoutesFromChildren, matchRoutes, useLocation, useNavigationType} from 'react-router-dom'
import {isAbortError, isChunkLoadError} from './ApiError'
import {handleChunkLoadReload} from './chunkLoadHandler'
import {reportError} from './reportError'
import {CONSENT_CHANGED_EVENT, getConsent, subscribe} from '../cookieConsent/store'

// Sentry is non-essential tracking — it must NOT initialize until the user has explicitly
// consented to analytics. We listen for consent changes and lazy-init on grant; on revoke we
// call Sentry.close() and set a runtime flag every Sentry.* callsite checks.

let initialized = false
let enabled = false
let activeAppName: 'gui' | 'render' | null = null
let unhandledRejectionHandler: ((e: PromiseRejectionEvent) => void) | null = null
let windowErrorHandler: ((e: ErrorEvent) => void) | null = null

const NOISE_PATTERNS = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /^Script error\.?$/i,
  /Loading chunk [\d]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  // Thrown from inside a browser extension's monkey-patched HTMLCanvasElement.getContext
  // (canvas-fingerprint blockers), not from our bundle — three.js itself imports `log` correctly.
  /^log is not defined$/i,
]

const SENSITIVE_KEY_PATTERN = /(authorization|cookie|password|token|secret|api[-_]?key|x-signature)/i

const isNoise = (message: string | undefined): boolean => {
  if (!message) return false
  return NOISE_PATTERNS.some((p) => p.test(message))
}

const scrubHeaders = (headers: Record<string, string> | undefined): void => {
  if (!headers) return
  for (const key of Object.keys(headers)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) headers[key] = '[Filtered]'
  }
}

const scrubExtras = (extra: Record<string, unknown> | undefined): void => {
  if (!extra) return
  for (const key of Object.keys(extra)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) extra[key] = '[Filtered]'
  }
}

const installGlobalHandlers = (): void => {
  unhandledRejectionHandler = (e) => {
    const reason = e.reason
    if (isAbortError(reason)) return
    if (isChunkLoadError(reason)) {
      handleChunkLoadReload()
      return
    }
    reportError(reason, 'unhandledrejection')
  }
  windowErrorHandler = (e) => {
    if (isChunkLoadError(e.error)) {
      handleChunkLoadReload()
      return
    }
    if (isNoise(e.message)) return
    reportError(e.error ?? new Error(e.message), 'window.error')
  }
  window.addEventListener('unhandledrejection', unhandledRejectionHandler)
  window.addEventListener('error', windowErrorHandler)
}

const removeGlobalHandlers = (): void => {
  if (unhandledRejectionHandler) {
    window.removeEventListener('unhandledrejection', unhandledRejectionHandler)
    unhandledRejectionHandler = null
  }
  if (windowErrorHandler) {
    window.removeEventListener('error', windowErrorHandler)
    windowErrorHandler = null
  }
}

const initSentry = (appName: 'gui' | 'render'): void => {
  if (initialized) {
    enabled = true
    return
  }
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return
  initialized = true
  enabled = true
  activeAppName = appName

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      /^\//,
      /\/api\//,
      ...(import.meta.env.VITE_API_BASE_URL ? [new RegExp(import.meta.env.VITE_API_BASE_URL)] : []),
    ],
    // Free tier: 50 replays/mo — only record sessions that produced an error.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    initialScope: {tags: {app: appName}},
    denyUrls: [/extensions\//i, /chrome-extension:/i, /moz-extension:/i],
    beforeSend(event, hint) {
      if (!enabled) return null
      const ex = hint.originalException
      if (isAbortError(ex) || isChunkLoadError(ex)) return null
      const msg = event.message ?? (ex instanceof Error ? ex.message : undefined)
      if (isNoise(msg)) return null
      scrubHeaders(event.request?.headers)
      scrubExtras(event.extra)
      return event
    },
  })

  installGlobalHandlers()
}

const disableSentry = (): void => {
  if (!initialized || !enabled) return
  enabled = false
  removeGlobalHandlers()
  // Sentry.close() flushes pending events and stops the client. It returns a Promise but we
  // don't await — best-effort teardown.
  void Sentry.close()
}

const applyConsent = (consented: boolean): void => {
  if (consented) {
    if (!activeAppName) return
    initSentry(activeAppName)
  } else {
    disableSentry()
  }
}

/**
 * Gate Sentry behind the user's analytics consent. Reads the synchronous LS-backed consent
 * store on call; subscribes to subsequent consent changes so toggling on /cookies takes
 * effect without a reload. Safe to call from main.tsx during module init.
 */
const initSentryIfConsented = (appName: 'gui' | 'render'): void => {
  if (typeof window === 'undefined') return
  activeAppName = appName
  const consent = getConsent()
  if (consent?.analytics) initSentry(appName)
  // React to user actions on /cookies or the global banner.
  subscribe(() => {
    const next = getConsent()
    applyConsent(next?.analytics === true)
  })
  // Also catch CustomEvents in case any cross-module path dispatches one.
  window.addEventListener(CONSENT_CHANGED_EVENT, () => {
    const next = getConsent()
    applyConsent(next?.analytics === true)
  })
}

const isSentryEnabled = (): boolean => enabled

export {initSentry, initSentryIfConsented, isSentryEnabled}
